import type { DateTime } from 'luxon';
import * as Yup from 'yup';

import { getDisplayTimezone } from '@core/configuration';

import type { Activity, ActivityWireFormat } from '../types/api/Activity/Activity';
import Enums from '../types/api/Enums';
import { User } from '../types/api/User/User';
import { FormatWallDateTime, ValidDateOrDefault } from '../utils/dateHelpers';
import { NIL_UUID } from '../utils/uuid';
import {
  TestForUndefined,
  YupBoolean,
  YupEnum,
  YupNullableEnum,
  YupNullableReference,
  YupNullableString,
  YupNullableDisplayZoneDate,
  YupReference,
  type YupSchemaNullableReferenceType,
  type YupSchemaReferenceType,
  YupString,
  YupDisplayZoneDate,
} from '../utils/yupHelpers';

export type ActivityBulkEditFormikType = {
  status: Enums.ActivityStatus | null;
  category: Enums.ActivityCategory | null;
  startTime: string | null;
  endTime: string | null;
  assignees: YupSchemaReferenceType[];
};

export type ActivitiesBulkEditRequestPayload = {
  id: string;
  status?: Activity['status'];
  startTime?: string;
  endTime?: string;
  category?: Activity['category'];
  assignees?: Pick<User, 'id'>[];
};

export type ActivityFormikType = Omit<
  ActivityWireFormat,
  'project' | 'companies' | 'contacts' | 'assignees' | 'creator'
> & {
  project: YupSchemaNullableReferenceType;
  companies: YupSchemaReferenceType[];
  contacts: YupSchemaReferenceType[];
  assignees: YupSchemaReferenceType[];
  // creator is omitted; it's not currently displayed or editable.
};

export const ActivityBulkEditSchemaFormik: Yup.SchemaOf<ActivityBulkEditFormikType> =
  Yup.object().shape({
    status: YupNullableEnum(Enums.ActivityStatus, 'Status'),
    category: YupNullableEnum(Enums.ActivityCategory, 'Category'),
    startTime: YupNullableDisplayZoneDate('Start time'),
    endTime: YupNullableDisplayZoneDate('End time'),
    assignees: Yup.array().of(YupReference('Assignee')),
  });

type ActivitySchemaBaseType = Omit<ActivityFormikType, 'id'>;

export const BaseActivitySchema: Yup.SchemaOf<ActivitySchemaBaseType> = Yup.object({
  name: YupString('Name'),
  isArchived: YupBoolean('Archived'),
  description: YupNullableString('Description'),
  category: YupEnum(Enums.ActivityCategory, 'Category'),
  status: YupEnum(Enums.ActivityStatus, 'Status'),
  startTime: YupDisplayZoneDate('Start time'),
  endTime: YupDisplayZoneDate('End time'),
  project: YupNullableReference('Project'),
  companies: Yup.array().of(YupReference('Company')),
  contacts: Yup.array().of(YupReference('Contact')),
  assignees: Yup.array().of(YupReference('Assignee')),
});

export const ActivitySchema: Yup.SchemaOf<ActivitySchemaBaseType> = Yup.object()
  .concat(BaseActivitySchema)
  .test(TestForUndefined('ActivitySchema'));

export const FormikActivity = (
  defaults: { currentDate: DateTime },
  activityData: Partial<Activity> | undefined,
): ActivityFormikType => {
  const zone = getDisplayTimezone();
  const nextHour = defaults.currentDate.setZone(zone).startOf('hour').plus({ hours: 1 });
  return {
    id: activityData?.id ?? NIL_UUID,
    isArchived: activityData?.isArchived ?? false,
    name: activityData?.name ?? '',
    description: activityData?.description ?? null,
    // Full precision, not minute-truncated: an untouched field must round-trip its original
    // seconds, else saving an unrelated edit silently shifts the activity's reminder.
    startTime: FormatWallDateTime(
      ValidDateOrDefault(activityData?.startTime?.setZone(zone), nextHour),
    ),
    endTime: FormatWallDateTime(
      ValidDateOrDefault(activityData?.endTime?.setZone(zone), nextHour.plus({ minutes: 30 })),
    ),
    category: activityData?.category ?? Enums.ActivityCategory.Task,
    status: activityData?.status ?? Enums.ActivityStatus.Planned,
    project: {
      id: activityData?.project?.id ?? null,
      option:
        activityData?.project === undefined || activityData?.project === null
          ? null
          : {
              value: activityData.project.id,
              label: activityData.project.name,
            },
    },
    companies: (activityData?.companies ?? []).map(({ id, name }) => ({
      id,
      option: {
        value: id,
        label: name,
      },
    })),
    contacts: (activityData?.contacts ?? []).map((c) => ({
      id: c.id,
      option: {
        value: c.id,
        label: c.fullName(),
      },
    })),
    assignees: (activityData?.assignees ?? []).map((u) => ({
      id: u.id,
      option: {
        value: u.id,
        label: u.fullName(),
      },
    })),
  };
};
